import { Injectable } from '@angular/core';
import { AppAuthService } from './auth.service';

@Injectable()
export class KeycloakAdapterService {
  constructor(private appAuth: AppAuthService) {}

  isLoggedIn(): Promise<boolean> {
    return Promise.resolve(this.appAuth.hasValidAccessToken() || !!sessionStorage.getItem('manual_oidc_access_token'));
  }

  getToken(): Promise<string> {
    const token = this.appAuth.getAccessToken() || sessionStorage.getItem('manual_oidc_access_token') || '';
    return Promise.resolve(token);
  }

  login(options?: any): Promise<void> {
    const redirect = options && options.redirectUri ? options.redirectUri : undefined;
    this.appAuth.loginWithRedirect(redirect, { prompt: 'login' });
    return Promise.resolve();
  }

  logout(redirectUri?: string): Promise<void> {
    this.appAuth.logout(redirectUri);
    return Promise.resolve();
  }

  loadUserProfile(): Promise<any> {
    return Promise.resolve(this.appAuth.getIdentityClaims());
  }

  /**
   * Checks if the current user has the given role.
   * The original KeycloakService supports an optional resource/client parameter; we ignore it
   * and just check against the roles available in the ID token.
   */
  isUserInRole(role: string, resource?: string): boolean {
    const roles = this.getUserRoles(true) || [];

    if (!role) {
      return false;
    }

    const target = String(role).trim();
    // tolerant comparisons: exact, case-insensitive, and with/without ROLE_ prefix
    const candidates = new Set<string>([
      target,
      target.toUpperCase(),
      target.toLowerCase(),
      `ROLE_${target}`,
      `ROLE_${target.toUpperCase()}`
    ]);

    for (const r of roles) {
      if (!r) { continue; }
      const vr = String(r).trim();
      if (candidates.has(vr) || candidates.has(vr.toUpperCase()) || candidates.has(vr.toLowerCase())) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns an array of roles. Kept signature compatible with keycloak-angular's
   * getUserRoles(allRoles = true) which returns a string[].
   */
  getUserRoles(allRoles: boolean = true): string[] {
    try {
      // Primary source: AppAuthService consolidated roles
      let roles = this.appAuth.getUserRoles() || [];



      // Fallback: try to derive roles from raw identity claims if the above is empty
      if ((!roles || roles.length === 0) && (this.appAuth as any).getIdentityClaims) {
        try {
          const claims = (this.appAuth as any).getIdentityClaims();
          const fallback: string[] = [];
          if (claims) {
            try {
              if (claims.realm_access && Array.isArray(claims.realm_access.roles)) {
                fallback.push(...claims.realm_access.roles);
              }
            } catch (e) {}
            try {
              const ra = claims.resource_access || {};
              for (const k of Object.keys(ra)) {
                if (ra[k] && Array.isArray(ra[k].roles)) {
                  fallback.push(...ra[k].roles);
                }
              }
            } catch (e) {}
          }
          if (fallback.length) {
            roles = fallback;
          }
        } catch (e) {
          // ignore fallback errors
        }
      }

      // Normalize and dedupe roles (trim strings)
      const normalized = Array.from(new Set((roles || []).map(r => (r || '').toString().trim()).filter(r => !!r)));

      return normalized;
    } catch (e) {
      return [];
    }
  }

  /**
   * Return parsed token (claims) if available — compatible with KeycloakService.getTokenParsed().
   */
  getTokenParsed(): any {
    return this.appAuth.getIdentityClaims();
  }

  /**
   * Returns a minimal Keycloak instance placeholder for compatibility. Some callers
   * only check for existence; full instance features are not provided.
   */
  getKeycloakInstance(): any {
    return null;
  }

  /**
   * Return a username derived from the identity claims when present.
   */
  getUsername(): string | null {
    const claims = this.appAuth.getIdentityClaims();
    if (!claims) {
      return null;
    }
    return claims.preferred_username || claims.email || claims.name || null;
  }

  /**
   * Attempt to update/refresh token. We try a best-effort: if the AppAuthService
   * reports a valid token we return true; otherwise resolve false. This keeps callers
   * that expect a Promise<boolean> working.
   */
  updateToken(minValidity?: number): Promise<boolean> {
    try {
      const valid = this.appAuth.hasValidAccessToken();
      return Promise.resolve(valid);
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  /**
   * No-op register to keep compatibility with KeycloakService.register when called.
   */
  register(options?: any): Promise<void> {
    return Promise.resolve();
  }

}
